import { useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Settings, Key, Shield, ExternalLink, Globe, Mail, CreditCard, Database
} from "lucide-react";

const AdminSettings = () => {
  const [showCredentials, setShowCredentials] = useState(false);

  const integrations = [
    {
      name: "Razorpay",
      description: "Payment gateway for registrations",
      icon: CreditCard,
      status: "Active",
      color: "bg-blue-500"
    },
    {
      name: "Resend",
      description: "Email service for confirmations",
      icon: Mail,
      status: "Active",
      color: "bg-green-500"
    },
    {
      name: "MongoDB",
      description: "Database for all data storage",
      icon: Database,
      status: "Active",
      color: "bg-emerald-500"
    }
  ];

  const quickLinks = [
    { name: "View Website", url: "/", icon: Globe },
    { name: "Razorpay Dashboard", url: "https://dashboard.razorpay.com", icon: CreditCard, external: true },
    { name: "Resend Dashboard", url: "https://resend.com/emails", icon: Mail, external: true },
  ];

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A0A0A] font-['Outfit']">Settings</h1>
        <p className="text-[#4B5563]">Manage admin settings and integrations</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Admin Credentials */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-[#FF3B30]" />
              Admin Credentials
            </CardTitle>
            <CardDescription>
              Your admin login credentials
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value="admin" disabled />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <div className="relative">
                <Input 
                  type={showCredentials ? "text" : "password"} 
                  value="finrun@2026" 
                  disabled 
                />
                <button
                  onClick={() => setShowCredentials(!showCredentials)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] hover:text-[#0A0A0A]"
                >
                  {showCredentials ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            <p className="text-xs text-[#4B5563]">
              To change credentials, update ADMIN_USERNAME and ADMIN_PASSWORD in backend/.env
            </p>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-[#007AFF]" />
              Quick Links
            </CardTitle>
            <CardDescription>
              Useful links and dashboards
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {quickLinks.map((link, i) => {
                const Icon = link.icon;
                return link.external ? (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-[#4B5563]" />
                      <span className="font-medium">{link.name}</span>
                    </div>
                    <ExternalLink className="w-4 h-4 text-[#4B5563]" />
                  </a>
                ) : (
                  <Link
                    key={i}
                    to={link.url}
                    className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-[#4B5563]" />
                      <span className="font-medium">{link.name}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Integrations Status */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#00C853]" />
              Integrations Status
            </CardTitle>
            <CardDescription>
              Connected services and their status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {integrations.map((integration, i) => {
                const Icon = integration.icon;
                return (
                  <div 
                    key={i}
                    className="flex items-center gap-4 p-4 bg-[#FAFAFA] rounded-xl"
                  >
                    <div className={`w-12 h-12 ${integration.color} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0A0A0A]">{integration.name}</p>
                      <p className="text-sm text-[#4B5563]">{integration.description}</p>
                      <span className="inline-flex items-center gap-1 text-xs text-green-600 mt-1">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                        {integration.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Admin Panel Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#FFD700]" />
              Admin Panel Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { title: "Dashboard", desc: "View stats and recent activity" },
                { title: "Registrations", desc: "Manage runner registrations, export CSV" },
                { title: "Gallery", desc: "Add/remove photos & videos for past events" },
                { title: "Hero Video", desc: "Set homepage background video" },
                { title: "Sponsors", desc: "Track and manage sponsor inquiries" },
                { title: "Contacts", desc: "View and respond to messages" },
                { title: "Email Tickets", desc: "Auto-sent with QR codes on payment" },
                { title: "Revenue Tracking", desc: "Track payments and revenue" }
              ].map((feature, i) => (
                <div key={i} className="p-4 border rounded-xl">
                  <p className="font-semibold text-[#0A0A0A] mb-1">{feature.title}</p>
                  <p className="text-sm text-[#4B5563]">{feature.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettings;
